import { Flex } from '@chakra-ui/react';
import { Button, FormFeedback, Input, useToast } from '@morressier/alembic';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { SubmitHandler, useForm } from 'react-hook-form';
import { sendTestEmail } from '../../../../../api/emails';
import { Text } from '@chakra-ui/react';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface Props {
  template: {
    subject: string;
    body: string;
    attachments?: ISubmissionFile[];
  };
  organizationId: string;
}
interface TestEmail {
  testEmail: string;
}

const schema = z.object({
  testEmail: z
    .string()
    .min(1, 'Please enter email address')
    .email('Please enter a valid email address')
});

const TestEmailSent = ({ template, organizationId }: Props) => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<TestEmail>({
    defaultValues: {
      testEmail: ''
    },
    resolver: zodResolver(schema)
  });

  const onSubmit: SubmitHandler<TestEmail> = async data => {
    try {
      const attachments = (template.attachments ?? [])
        .filter(att => att?.name && att?.url)
        .map(att => ({ name: att.name, url: att.url }));

      const emailContent = {
        subject: template.subject,
        body: template.body,
        recipients: {
          to: data.testEmail,
          cc: [],
          bcc: []
        },
        attachments
      };

      await sendTestEmail(organizationId, emailContent);

      toast({
        variant: 'success',
        title: `Test email successfully sent to ${emailContent.recipients.to}.`,
        status: 'success'
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error while sending test email',
        description: 'Please try again later.',
        status: 'error'
      });
    }
  };
  return (
    <Flex flexDirection="column" gap={4} marginBottom={2}>
      <Flex flexDirection="column">
        <Text textStyle="headingGroup">Test Email</Text>
        <Text textStyle="headingContext">
          Preview your email as its appear in recipients’ inboxes.
        </Text>
      </Flex>
      <Flex gap={2} flexDirection={{ base: 'column', md: 'row' }}>
        <Input
          {...register('testEmail', {
            required: 'Please enter email address'
          })}
          type="text"
          placeholder="Email address"
          isInvalid={!!errors.testEmail?.message}
        />
        <Button
          variant="secondary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting}
          loadingText="Sending..."
          flexShrink={0}
        >
          Send Test
        </Button>
      </Flex>
      {errors.testEmail?.message && (
        <FormFeedback variant="error">{errors.testEmail?.message}</FormFeedback>
      )}
    </Flex>
  );
};

export default TestEmailSent;
